import React from 'react';

import { insightsRouteConfig } from 'src/app/graph/routes';
import { ErrorBoundary } from 'src/components/common/errorBoundary/ErrorBoundary';
import { useLocalStorage } from 'src/hooks/useLocalStorage';
import { useRouteParams } from 'src/hooks/useRouteParams';
import { TimeRangeOption } from 'src/lib/config/timeRangeOptions';
import { useLDFlag } from 'src/lib/launchDarkly';

import { RedirectToDefaultTab } from '../redirectToDefaultTab/RedirectToDefaultTab';
import { FederatedErrors } from '../section-report/errors-section/FederatedErrors';
import { ErroredPathsWithData } from '../section-report/errors-section/LegacyErroredPathsWithData';
import { ServiceReport } from '../section-report/performance-section/serviceReport/ServiceReport';
import { QueryTraces } from '../section-report/traces-section/queryTraces/QueryTraces';

interface TabShape {
  title?: string;
  isAvailableInDemo: boolean;
  isOperationLevel: boolean;
  isServiceLevel: boolean;
  value: string;
}

export function ServiceReportPane({
  serviceId,
  schemaTag,
  selectedRange,
  isTracingAvailable,
  clickableTabs,
  defaultTab,
  isSubscription,
}: {
  serviceId: string;
  schemaTag: string;
  selectedRange: TimeRangeOption;
  isTracingAvailable: boolean;
  clickableTabs: Array<TabShape>;
  defaultTab: TabShape;
  isSubscription: boolean;
  isSupergraphVariant: boolean;
}) {
  const { tab, trace, traceBucket, query, to, from } =
    useRouteParams(insightsRouteConfig);
  const [migrationView] = useLocalStorage('errorInsightsMigrationView');
  const migratedToNewErrorsView = useLDFlag('insights-error-categorization-ui');
  // If the LD flag is off, we're allowing users to toggle back and forth
  // via the property in local storage.
  const showErrorCategorization =
    migratedToNewErrorsView || migrationView === 'categorized';
  const activeTab = tab || defaultTab.value;

  if (
    !clickableTabs.find((t) => t.value === activeTab) ||
    tab === defaultTab.value
  ) {
    return <RedirectToDefaultTab />;
  }

  let SectionContent;
  switch (activeTab) {
    case 'usage':
      SectionContent = (
        <ServiceReport
          queryId={query}
          range={selectedRange}
          graphId={serviceId}
          isSubscription={isSubscription}
        />
      );
      break;
    case 'errors':
      SectionContent = showErrorCategorization ? (
        <FederatedErrors />
      ) : (
        <ErroredPathsWithData range={selectedRange} graphId={serviceId} />
      );
      break;
    case 'traces':
      if (!query) {
        // We should not be in `traces` if there is no `query`
        return <RedirectToDefaultTab />;
      }

      SectionContent = (
        <QueryTraces
          key={JSON.stringify({
            from,
            query,
            selectedRange,
            serviceId,
            to,
          })}
          isTracingAvailable={isTracingAvailable}
          range={selectedRange}
          serviceId={serviceId}
          schemaTag={schemaTag}
          queryId={query}
          traceId={trace}
          currentBucket={traceBucket ? parseInt(traceBucket, 10) : null}
        />
      );
      break;
    case 'operation':
      return <RedirectToDefaultTab />;
    default:
      throw new Error('Tab in config that is not listed in ServiceReportPane');
  }

  return (
    <div className="w-full flex-1 bg-primary p-0">
      <ErrorBoundary>
        <div className="min-h-full">{SectionContent}</div>
      </ErrorBoundary>
    </div>
  );
}
